import { useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useSubmitPriceLine } from '../../hooks';
import { formatDateTime, formatPercent } from '../../lib/format';
import { governingLine, BASE_SLOT } from '../../lib/priceSlots';
import type { PriceSlot } from '../../lib/priceSlots';
import type { PriceLineResponse } from '../../model/types';

interface TierRow {
  minQuantity: number;
  amount: string;
}

/** The customer-group axis (adr-pricing-009) — composes with `base`/`channel`, never with `list`
 * (structural exclusivity, design spec §8.5/§8.10). */
export interface PriceCellEditorGroupContext {
  code: string;
  name?: string;
}

/**
 * Mutually exclusive by construction (design spec §8.6) — a cell/line is either the base slot, a
 * channel's slot, or (adr-pricing-025) a price list's slot. `'list'` never carries a
 * `channelCode`/`customerGroupCode` on submit (the exclusivity CHECK) and the form must not grow
 * channel/group affordances in that mode. `base`/`channel` additionally carry an OPTIONAL `group`
 * (plan-platform-002 WP4.3) — the group axis composes with those two, never with `list`.
 */
export type PriceCellEditorContext =
  | { type: 'base'; group?: PriceCellEditorGroupContext }
  | { type: 'channel'; code: string; name?: string; group?: PriceCellEditorGroupContext }
  | { type: 'list'; code: string; name: string };

export interface PriceLineEditorFormProps {
  product: { id: string; name?: string };
  context: PriceCellEditorContext;
  existingLines?: PriceLineResponse[];
  /**
   * A scheduled or otherwise non-governing line to seed and edit explicitly (design spec §8.3's
   * base scheduled row, §8.5's "other prices" rows) — overrides the default governing-line seed.
   * Its scheduled-edit rule (§8.4) always RESENDS `validFrom`, never treating it as an untouched
   * seed to omit on save.
   */
  targetLine?: PriceLineResponse;
  /** Renders every control disabled under a lock `Alert`; the footer shows only a Close action. */
  readOnly?: boolean;
  /**
   * An additional, already-translated caption line shown under the main blast-radius caption
   * (design spec §8.4) — used when the form is opened from the products-list scope selector in
   * `store:` scope (plan-merchant-portal-018 WP2.3) to name HOW the selected store actually
   * receives this price: via a bound price list, a channel, or as the base price. The caller (the
   * only party that knows the current list scope AND has the registries to resolve names) computes
   * the ready string; this form stays scope-agnostic and just renders whatever it's handed. Absent
   * in every other context (the grid, the price-list detail page, the card drawer).
   */
  storeCaption?: string;
  /** Fired after a successful save with a ready-to-display, already-translated message. */
  onSaved: (message: string) => void;
  /** Fired when the merchant cancels without saving, or closes a read-only form. */
  onCancel: () => void;
}

function toIso(local: string): string | undefined {
  if (!local.trim()) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toLocal(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

/** The context's own slot descriptor (design spec §8.0) — feeds `governingLine` so seeding is a
 * governing-by-validity lookup for EVERY context, not only the base slot (the R0 leak class, now
 * closed for non-base slots too: an unguarded array-order `.find()` could seed the form from a
 * scheduled or otherwise non-governing line ahead of the one actually in force). */
function slotOfContext(context: PriceCellEditorContext): PriceSlot {
  if (context.type === 'list') return { priceListCode: context.code };
  if (context.type === 'channel') return { channelCode: context.code, customerGroupCode: context.group?.code };
  return { customerGroupCode: context.group?.code };
}

function seedTiers(target: PriceLineResponse | undefined): TierRow[] {
  if (target && target.tiers.length > 0) {
    return target.tiers.map((t) => ({ minQuantity: t.minQuantity, amount: String(t.amount) }));
  }
  return [{ minQuantity: 1, amount: '' }];
}

/**
 * The one price editor for every context (adr-merchant-portal-018 Decision 1) — extracted from the
 * shipped `PriceCellEditorDrawer` so cost/floor/scheduled-line handling lands once for the grid, the
 * price-list detail page, the products-list quick edit AND the card's `ProductPricesDrawer`, instead
 * of forking. Owns the seeding effect, validation, the blast-radius caption, cost → margin/markup
 * and the min-sale floor, the scheduled-line rule, read-only mode, and the submit itself
 * (`POST /catalog/pricing/price-lines`, `supersedeOverlapping: true`, list/group exclusivity on the
 * wire — adr-merchant-portal-010's semantics, unchanged).
 *
 * Deliberately has NO `open` prop: both hosts (the right-anchored shell, `ProductPricesDrawer`'s
 * level 2) only ever render this component while it should be showing fresh data — MUI's `Drawer`
 * unmounts its content on close by default, and `ProductPricesDrawer` conditionally renders this
 * component only at level 2 — so a fresh mount already re-runs the seeding effect from scratch every
 * time the form should "open" again, with no extra prop needed to gate it.
 */
export function PriceLineEditorForm({
  product,
  context,
  existingLines,
  targetLine,
  readOnly = false,
  storeCaption,
  onSaved,
  onCancel,
}: PriceLineEditorFormProps) {
  const { t } = useTranslation('catalog');
  const submit = useSubmitPriceLine();
  const pending = submit.isPending;

  const [tiers, setTiers] = useState<TierRow[]>([{ minQuantity: 1, amount: '' }]);
  const [compareAt, setCompareAt] = useState('');
  const [validFrom, setValidFrom] = useState('');
  // The value "Valid from" was seeded with on open — handleSave compares against it so an
  // UNTOUCHED seeded date is not resent (see the comment in the effect below) — UNLESS this is a
  // scheduled-line edit (§8.4), which always resends it (see `isScheduledEdit` below).
  const [seededValidFrom, setSeededValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [costAmount, setCostAmount] = useState('');
  const [costFromBase, setCostFromBase] = useState(false);
  const [minSaleAmount, setMinSaleAmount] = useState('');
  const [costSectionOpen, setCostSectionOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Extracted so the effect's dependency array below is a plain identifier list (ESLint's
  // exhaustive-deps rule cannot statically check an inline ternary expression).
  const contextSlotCode = context.type === 'base' ? undefined : context.code;
  const contextGroupCode = context.type === 'list' ? undefined : context.group?.code;

  useEffect(() => {
    // One `now` for every lookup below, so a seed is internally consistent within a single mount
    // (design spec §8.0 — governing-by-validity, never array order).
    const now = new Date();
    const lines = existingLines ?? [];
    const target = targetLine ?? governingLine(lines, slotOfContext(context), now);

    setTiers(seedTiers(target));
    setCompareAt(target?.compareAtAmount != null ? String(target.compareAtAmount) : '');
    // Seed BOTH ends of the validity window for display — the merchant must see when the
    // current/scheduled line starts, not just when it ends. validFrom is display-seeded only:
    // handleSave resends it solely when the merchant edits it (or this is a scheduled-line edit —
    // see isScheduledEdit below), because an untouched start date must NOT be resent on an ordinary
    // save — with supersedeOverlapping the replacement line takes effect "now" rather than being
    // backdated to the old line's start (contract-tested in PriceLineEditorForm.test.tsx).
    const seededFrom = toLocal(target?.validFrom);
    setValidFrom(seededFrom);
    setSeededValidFrom(seededFrom);
    setValidTo(toLocal(target?.validTo));

    // Cost & floor (design spec §8.4): seed from the target/governing line of the SAME slot; for a
    // non-base slot with no own cost, prefill from the base line's costAmount instead (cost is a
    // product fact the merchant rarely wants to retype per channel/list/group) — flagged via
    // costFromBase so the helper text can attribute it to the base line, and the value is still
    // SENT explicitly on save, never implied.
    const ownCost = target?.costAmount;
    if (ownCost != null) {
      setCostAmount(ownCost);
      setCostFromBase(false);
    } else if (context.type !== 'base') {
      const baseLine = governingLine(lines, BASE_SLOT, now);
      setCostAmount(baseLine?.costAmount ?? '');
      setCostFromBase(Boolean(baseLine?.costAmount));
    } else {
      setCostAmount('');
      setCostFromBase(false);
    }
    setMinSaleAmount(target?.minSaleAmount ?? '');
    setCostSectionOpen(Boolean(ownCost || target?.minSaleAmount));
    setSubmitError(null);
    // `context` is intentionally NOT a dependency (only its stable slot-identifying fields are):
    // callers construct a fresh `context` object literal every render (e.g. `PriceGridPanel`'s
    // `buildContext`), so depending on the whole object would re-run this effect — and wipe any
    // in-progress edit — on every unrelated parent re-render while the form stays mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLines, context.type, contextSlotCode, contextGroupCode, product.id, targetLine?.id]);

  const unitAmountStr = tiers[0]?.amount ?? '';
  const unitAmount = parseFloat(unitAmountStr);
  const wholesaleValid = tiers.slice(1).every((t) => {
    const amt = parseFloat(t.amount);
    return t.minQuantity >= 2 && !Number.isNaN(amt) && amt > 0;
  });
  // Cross-tier validation: beyond each row's own qty/amount validity, the ladder as a WHOLE must
  // make commercial sense — minQuantity unique and ascending row over row (a lower/duplicate
  // threshold than the row above it is nonsensical), and amount non-increasing as qty rises
  // (buying more must never cost more per unit). Compared over the same "complete" rows
  // `handleSave` would actually submit — parsed the same way — so a still-blank row mid-edit never
  // flashes this warning early, and the check never disagrees with what would be sent.
  const completeTiers = tiers
    .map((t, i) => ({ minQuantity: i === 0 ? 1 : t.minQuantity, amount: parseFloat(t.amount) }))
    .filter((t) => t.minQuantity >= 1 && !Number.isNaN(t.amount) && t.amount > 0);
  const tierOrderValid = completeTiers.every(
    (t, i) => i === 0 || (t.minQuantity > completeTiers[i - 1].minQuantity && t.amount <= completeTiers[i - 1].amount),
  );

  // Scheduled-line edit rule (design spec §8.4): editing a line whose window has not started yet
  // always requires and resends validFrom — the shipped "untouched seeded validFrom is not resent"
  // optimization would otherwise turn this edit into a replacement of the CURRENT line instead.
  const isScheduledEdit = Boolean(targetLine?.validFrom && new Date(targetLine.validFrom) > new Date());
  const validFromMissing = isScheduledEdit && validFrom.trim() === '';

  // Cost & floor validation (design spec §8.4) — costAmount/minSaleAmount are Money STRINGS on the
  // wire; parsed here only to validate/compute margin, never persisted as arithmetic.
  const costNum = parseFloat(costAmount);
  const hasCost = costAmount.trim() !== '' && !Number.isNaN(costNum);
  const minSaleNum = parseFloat(minSaleAmount);
  const hasMinSale = minSaleAmount.trim() !== '' && !Number.isNaN(minSaleNum);
  const priceValid = unitAmountStr !== '' && !Number.isNaN(unitAmount) && unitAmount > 0;
  const minSaleAboveError = hasMinSale && priceValid && minSaleNum > unitAmount;
  const belowCost = hasCost && priceValid && costNum >= unitAmount;
  const marginPct = hasCost && priceValid && unitAmount > 0 ? ((unitAmount - costNum) / unitAmount) * 100 : undefined;
  const markupPct = hasCost && priceValid && costNum > 0 ? ((unitAmount - costNum) / costNum) * 100 : undefined;

  const isValid =
    priceValid && wholesaleValid && tierOrderValid && !minSaleAboveError && !validFromMissing;

  const setTierAmount = (index: number, value: string) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, amount: value } : t)));
  };

  const setTierQty = (index: number, value: string) => {
    const qty = parseInt(value, 10);
    if (!Number.isNaN(qty) && qty > 0) {
      setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, minQuantity: qty } : t)));
    }
  };

  const addTier = () => {
    setTiers((prev) => {
      const maxQty = prev.reduce((max, t) => Math.max(max, t.minQuantity), 0);
      const nextQty = maxQty > 0 ? maxQty + 1 : 10;
      return [...prev, { minQuantity: nextQty, amount: '' }];
    });
  };

  const removeTier = (index: number) => {
    if (index === 0) return;
    setTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const ca = parseFloat(compareAt);
    setSubmitError(null);
    try {
      await submit.mutateAsync({
        productId: product.id,
        // Mutually exclusive by construction (design spec §8.6): a list-context save carries
        // ONLY priceListCode — never a channelCode/customerGroupCode key at all alongside it
        // (not even `undefined`-valued — the key itself is absent in list context). The optional
        // group axis (plan-platform-002 WP4.3) composes with base/channel only.
        channelCode: context.type === 'channel' ? context.code : undefined,
        priceListCode: context.type === 'list' ? context.code : undefined,
        ...(context.type !== 'list' && context.group
          ? { customerGroupCode: context.group.code }
          : {}),
        compareAtAmount: !Number.isNaN(ca) && ca > 0 ? ca : undefined,
        // Send validFrom only when the merchant changed it (incl. cleared it → undefined =
        // effective now), UNLESS this is a scheduled-line edit, which always resends it (§8.4).
        validFrom: isScheduledEdit || validFrom !== seededValidFrom ? toIso(validFrom) : undefined,
        validTo: toIso(validTo),
        supersedeOverlapping: true,
        tiers: completeTiers,
        costAmount: costAmount.trim() || undefined,
        minSaleAmount: minSaleAmount.trim() || undefined,
      });
      onSaved(t('priceCellEditorDrawer.saved'));
    } catch (err: unknown) {
      setSubmitError((err as NormalizedApiError).message ?? t('priceCellEditorDrawer.saveError'));
    }
  };

  // Blast-radius caption (design spec §8.4) — always shown, context-driven. List context reuses the
  // EXISTING listContextHint copy verbatim (the shell used to render it in its own header; it moves
  // here so `ProductPricesDrawer`'s level 2 gets the same explanation the shell always gave).
  const scopeCaption = (() => {
    if (context.type === 'list') return t('priceCellEditorDrawer.listContextHint');
    if (context.type === 'channel' && context.group) {
      return t('priceCellEditorDrawer.scope.channelGroup', {
        group: context.group.name ?? context.group.code,
        channel: context.name ?? context.code,
      });
    }
    if (context.type === 'channel') {
      return t('priceCellEditorDrawer.scope.channel', { name: context.name ?? context.code });
    }
    if (context.type === 'base' && context.group) {
      return t('priceCellEditorDrawer.scope.group', { name: context.group.name ?? context.group.code });
    }
    return t('priceCellEditorDrawer.scope.base');
  })();

  return (
    <>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {readOnly ? (
            <Alert severity="info" icon={<LockRoundedIcon fontSize="inherit" />} sx={{ fontSize: 13 }}>
              {t('priceCellEditorDrawer.readOnlyNotice')}
            </Alert>
          ) : null}

          {/* Scheduled-line edit rule (§8.4) replaces the ordinary blast-radius caption while
              editing a not-yet-effective line — validFrom becomes required and is always resent. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              {isScheduledEdit
                ? t('priceCellEditorDrawer.scheduledTitle', { date: formatDateTime(targetLine?.validFrom) })
                : scopeCaption}
            </Typography>
            {targetLine?.priceKind === 'MARKDOWN' ? (
              <Chip size="small" variant="outlined" label={t('priceCellEditorDrawer.markdownChip')} />
            ) : null}
          </Stack>
          {isScheduledEdit ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              {t('priceCellEditorDrawer.scheduledHint')}
            </Typography>
          ) : null}

          {/* Store-scope caption (design spec §8.4, WP2.3) — names HOW the products-list's
              currently-selected store receives this price; absent everywhere else. */}
          {storeCaption ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              {storeCaption}
            </Typography>
          ) : null}

          {/* Unit price + compare-at */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('priceCellEditorDrawer.priceLabel')}
              required
              type="number"
              value={tiers[0]?.amount ?? ''}
              onChange={(e) => setTierAmount(0, e.target.value)}
              error={unitAmountStr !== '' && !priceValid}
              disabled={readOnly}
              slotProps={{ htmlInput: { 'data-testid': 'unit-amount-input', min: 0, step: '0.01' } }}
              fullWidth
            />
            <TextField
              label={t('priceCellEditorDrawer.oldPriceLabel')}
              type="number"
              value={compareAt}
              onChange={(e) => setCompareAt(e.target.value)}
              disabled={readOnly}
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
              fullWidth
            />
          </Stack>

          {/* Validity window */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('priceCellEditorDrawer.validFromLabel')}
              type="datetime-local"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              fullWidth
              disabled={readOnly}
              required={isScheduledEdit}
              error={validFromMissing}
              helperText={validFromMissing ? t('priceCellEditorDrawer.validFromRequired') : undefined}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'data-testid': 'valid-from-input' },
              }}
            />
            <TextField
              label={t('priceCellEditorDrawer.validToLabel')}
              type="datetime-local"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              fullWidth
              disabled={readOnly}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          {/* Governance hint — byte-identical in every context (adr-pricing-025 d7: list-line
              governance is the same as any other line). */}
          <Alert severity="warning" icon="⚠" sx={{ fontSize: 13 }}>
            <Trans t={t} i18nKey="priceCellEditorDrawer.governanceHint" components={{ strong: <strong /> }}>
              When saved, the price goes through <strong>validation (validate-on-write)</strong>: a
              sharp drop will require <strong>approval</strong> instead of publishing immediately.
            </Trans>
          </Alert>

          <Divider />

          {/* Cost & margin (design spec §8.4, R3) — collapsible, open by default only when a value
              was seeded, so a fresh base price doesn't lead with a section that has nothing in it. */}
          <Box>
            <Button
              onClick={() => setCostSectionOpen((v) => !v)}
              endIcon={
                <ExpandMoreRoundedIcon
                  fontSize="small"
                  sx={{ transform: costSectionOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                />
              }
              size="small"
              variant="text"
              sx={{ pl: 0 }}
              aria-expanded={costSectionOpen}
            >
              {t('priceCellEditorDrawer.costSection')}
            </Button>
            <Collapse in={costSectionOpen}>
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('priceCellEditorDrawer.costLabel')}
                    type="number"
                    value={costAmount}
                    onChange={(e) => {
                      setCostAmount(e.target.value);
                      setCostFromBase(false);
                    }}
                    disabled={readOnly}
                    helperText={costFromBase ? t('priceCellEditorDrawer.costFromBase') : t('priceCellEditorDrawer.costHelper')}
                    slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                    fullWidth
                  />
                  <TextField
                    label={t('priceCellEditorDrawer.minSaleLabel')}
                    type="number"
                    value={minSaleAmount}
                    onChange={(e) => setMinSaleAmount(e.target.value)}
                    disabled={readOnly}
                    error={minSaleAboveError}
                    helperText={
                      minSaleAboveError
                        ? t('priceCellEditorDrawer.minSaleAboveError')
                        : t('priceCellEditorDrawer.minSaleHelper')
                    }
                    slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                    fullWidth
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color={belowCost ? 'warning.main' : 'text.secondary'}
                  aria-live="polite"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {belowCost ? <WarningAmberRoundedIcon fontSize="inherit" /> : null}
                  {belowCost
                    ? t('priceCellEditorDrawer.belowCost')
                    : marginPct !== undefined && markupPct !== undefined
                      ? t('priceCellEditorDrawer.marginLine', {
                          margin: formatPercent(marginPct),
                          markup: formatPercent(markupPct),
                        })
                      : t('priceCellEditorDrawer.marginUnavailable')}
                </Typography>
              </Stack>
            </Collapse>
          </Box>

          <Divider />

          {/* Wholesale tier editor */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('priceCellEditorDrawer.wholesaleTiers')}
          </Typography>

          {!tierOrderValid ? (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              {t('priceCellEditorDrawer.tierOrderWarning')}
            </Alert>
          ) : null}

          {tiers.slice(1).map((tier, i) => {
            const idx = i + 1;
            const rowAmt = parseFloat(tier.amount);
            const qtyInvalid = tier.minQuantity < 2;
            const amtInvalid = tier.amount !== '' && (Number.isNaN(rowAmt) || rowAmt <= 0);
            return (
              <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  label={t('priceCellEditorDrawer.fromQtyLabel')}
                  type="number"
                  value={tier.minQuantity || ''}
                  onChange={(e) => setTierQty(idx, e.target.value)}
                  error={qtyInvalid}
                  disabled={readOnly}
                  helperText={qtyInvalid ? t('priceCellEditorDrawer.minQtyHelper') : ' '}
                  slotProps={{ htmlInput: { min: 2, step: 1 } }}
                  sx={{ width: 110 }}
                />
                <TextField
                  label={t('priceCellEditorDrawer.priceLabel')}
                  type="number"
                  value={tier.amount}
                  onChange={(e) => setTierAmount(idx, e.target.value)}
                  error={amtInvalid}
                  disabled={readOnly}
                  helperText={amtInvalid ? t('priceCellEditorDrawer.tierPriceHelper') : ' '}
                  slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                  fullWidth
                />
                <IconButton
                  aria-label={t('priceCellEditorDrawer.removeTierAria')}
                  onClick={() => removeTier(idx)}
                  size="small"
                  disabled={readOnly}
                  sx={{ mt: 1 }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}

          {!readOnly ? (
            <Button
              startIcon={<AddRoundedIcon />}
              onClick={addTier}
              variant="text"
              size="small"
              sx={{ alignSelf: 'flex-start', pl: 0 }}
            >
              {t('priceCellEditorDrawer.addTier')}
            </Button>
          ) : null}

          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        {readOnly ? (
          <Button onClick={onCancel}>{t('common:action.close')}</Button>
        ) : (
          <>
            <Button onClick={onCancel} disabled={pending}>
              {t('common:action.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={!isValid || pending}
              startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('common:action.save')}
            </Button>
          </>
        )}
      </Box>
    </>
  );
}
